import React, { useEffect, useState } from 'react';
import { useAolStore } from 'src/context/AolContext';
import AolFilterHeaderComponent from '../../aol/aol-filter-header/AolFilterHeader';
import BunkeringDateListVisual from './BunkeringDateListVisual';
import { useBunkerContext } from 'src/context/BunkerContext';
import { AolFilterWrapper, BunkeringSidePanelWrapper } from './BunkeringDateList.styles';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import { formatAolUrlDate, getAolDateFromUrlFormat, getAolFromToTimeFromUrlFormat } from 'src/utils/aol/aolDateUtils';
import { isDateAfter, isDateBefore, isDateSame } from 'odfjell-date-time-utils/dateTimeUtils';
import { useAppStore } from 'src/context/AppContext';
import { useVesselStore } from 'src/context/VesselContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { BunkeringPageTabs } from '../BunkeringUtils';

const BunkeringDateListContainer: React.FC = () => {
    const { triggerQueryParamsUpdate, queryParamUpdate, getQueryParamValueFromUrl } = useQueryParamsHandler();
    const { aolGroups, aolFilter, setAolFilter, aolListSortAsc } = useAolStore();
    const { setSelectedBunkeringDate, selectedBunkeringDate, setSelectedBunkeringEventId, setSelectedBunkeringTab } = useBunkerContext();
    const { selectedVessel } = useVesselStore();
    const { appFeatureSettings } = useAppStore();

    const [bunkeringDates, setBunkeringDates] = useState<Date[]>([]);

    useEffect(() => {
        const fromDateStr = getQueryParamValueFromUrl('filterFrom');
        const toDateStr = getQueryParamValueFromUrl('filterTo');
        const aolDateStr = getQueryParamValueFromUrl('aolDate');
        if (fromDateStr === null || toDateStr === null) return;
        const fromDate = getAolFromToTimeFromUrlFormat(fromDateStr);
        const toDate = getAolFromToTimeFromUrlFormat(toDateStr);
        const aolDate = getAolDateFromUrlFormat(aolDateStr ?? '');

        if (!aolDate.getTime() || (aolDate?.getTime() && (isDateBefore(aolDate, fromDate) || isDateAfter(aolDate, toDate)))) {
            triggerQueryParamsUpdate([{ name: 'aolDate', newValue: formatAolUrlDate(toDate) } as QueryParamValueProps]);
            return;
        }

        if (fromDate?.getTime() && toDate?.getTime() && (!isDateSame(fromDate, aolFilter?.fromDate) || !isDateSame(toDate, aolFilter?.toDate)))
            setAolFilter({ fromDate: new Date(fromDate), toDate: new Date(toDate), isDefault: false });

        if (aolDate) setSelectedBunkeringDate(aolDate);
    }, [queryParamUpdate]);

    useEffect(() => {
        if (!aolFilter) return;
        const { fromDate, toDate } = aolFilter;
        const dates = [];
        const today = new Date();
        let featureEnabledAt = appFeatureSettings
            .find((f) => f.appFeatureId === AppFeatureEnum.Bunkering)
            ?.vessels?.find((v) => v.imoNumber === selectedVessel?.imoNumber?.toString())?.fromDate;
        if (!featureEnabledAt) featureEnabledAt = appFeatureSettings.find((f) => f.appFeatureId === AppFeatureEnum.Bunkering)?.fromDate;
        let _fromDate = fromDate;
        if (featureEnabledAt && isDateAfter(featureEnabledAt, fromDate)) _fromDate = featureEnabledAt;

        const currentDate = new Date(_fromDate);
        while (currentDate <= toDate && currentDate <= today) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        if (aolListSortAsc) dates.reverse();
        setBunkeringDates(dates);

        const fromDateStr = getQueryParamValueFromUrl('fromDate');
        const toDateStr = getQueryParamValueFromUrl('toDate');
        if (fromDateStr === null || toDateStr === null)
            triggerQueryParamsUpdate([
                { name: 'filterFrom', newValue: formatAolUrlDate(fromDate) },
                { name: 'filterTo', newValue: formatAolUrlDate(toDate) },
            ]);
    }, [aolFilter, aolListSortAsc, appFeatureSettings, selectedVessel?.imoNumber]);

    const onSelectDateFilter = (fromDate: Date, toDate: Date) => {
        triggerQueryParamsUpdate([
            { name: 'filterFrom', newValue: formatAolUrlDate(fromDate) },
            { name: 'filterTo', newValue: formatAolUrlDate(toDate) },
        ]);
    };

    const onSelectBunkeringDate = (date: Date) => {
        const aolDateStr = getQueryParamValueFromUrl('aolDate');
        const aolDate = getAolDateFromUrlFormat(aolDateStr ?? '');
        setSelectedBunkeringTab(BunkeringPageTabs.rob);
        if (isDateSame(date, aolDate)) return;
        setSelectedBunkeringEventId(null);
        triggerQueryParamsUpdate([{ name: 'aolDate', newValue: formatAolUrlDate(date) } as QueryParamValueProps]);
    };

    return (
        <BunkeringSidePanelWrapper>
            <AolFilterWrapper>
                <AolFilterHeaderComponent onChange={onSelectDateFilter} page={'bunkering'}></AolFilterHeaderComponent>
            </AolFilterWrapper>
            <BunkeringDateListVisual
                dates={bunkeringDates}
                onSelectCalendarDate={onSelectBunkeringDate}
                aolGroups={aolGroups}
                selectedBunkeringDate={selectedBunkeringDate}
            ></BunkeringDateListVisual>
        </BunkeringSidePanelWrapper>
    );
};

export default BunkeringDateListContainer;
